import {
  Button,
  Container,
  Form,
  Input,
  RadioGroup,
  Stack,
  Text,
  useBasisForm,
} from "basis";
import { CreditCard } from "components/CreditCard/CreditCard";
import { FormActions } from "components/FormActions/FormActions";
import { Cancel } from "components/IncreaseCreditLimit/Cancel";
import { CancelLink } from "components/IncreaseCreditLimit/components/CancelLink";
import { Layout } from "components/Layout/Layout";
import { Separator } from "components/Separator/Separator";
import { yesNoOptions } from "library/sharedOptions";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCliContext } from "../hooks/useCliContext";
import { ActionTypes, AdditionalDebtsType } from "../types";
import { nzValidateRadioGroupField } from "../validation/nzCustomValidation";

export const AdditionalDebts = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {
    state: { accountInfo, additionalDebts },
    dispatch,
  } = useCliContext();

  const navigate = useNavigate();
  const { methods, Field } = useBasisForm<AdditionalDebtsType>({
    defaultValues: additionalDebts,
  });

  const { watch } = methods;
  const { nzHaveOtherLoans, nzCurrentLoans, ...formValues } = watch();

  const onSubmit = (values: AdditionalDebtsType) => {
    dispatch({
      type: ActionTypes.SUBMIT_ADDITIONAL_DEBTS,
      additionalDebts: {
        ...values,
      },
    });
    navigate("/increase-credit-limit/review-submit");
  };

  return (
    <Layout>
      <Layout.Header>Request a credit limit increase</Layout.Header>
      <Layout.Main>
        <Form methods={methods} onSubmit={onSubmit}>
          <>
            <Stack gap="8">
              <Container bg="grey.t05" padding="4">
                <CreditCard
                  cardName={accountInfo?.cardName ?? ""}
                  cardEndsWith={accountInfo?.cardEndsWith}
                />
              </Container>
              <Stack gap="4">
                <Text>
                  <strong>Step 3 of 3</strong>
                </Text>
                <Text as="h3" textStyle="heading4">
                  Your other commitments
                </Text>
              </Stack>
              <Separator />
              <Text as="h4" textStyle="heading5" color="primary.blue.t100">
                Additional debts/Other loans
              </Text>
              <Container width="100%" width-sm="400">
                <Field
                  as={RadioGroup}
                  name="nzHaveOtherLoans"
                  label="Do you have any other personal loans, credit cards, or hire purchases?"
                  options={yesNoOptions}
                  validate={nzValidateRadioGroupField}
                />
              </Container>

              {nzHaveOtherLoans === "yes" && (
                <Stack gap="8" width="100%" width-sm="400">
                  <Field
                    as={Input}
                    name="nzOwingLoan"
                    label="Total amount owing on loans"
                    variant="numeric"
                    maxLength="8"
                    helpText="Monthly personal loan, hire purchase or credit card payments"
                    prefix="$"
                  />

                  <Field
                    as={RadioGroup}
                    name="nzCurrentLoans"
                    label="Do any of your current loans include a lump sum payment either during or at the end of term?"
                    options={yesNoOptions}
                    validate={nzValidateRadioGroupField}
                  />

                  {nzCurrentLoans === "yes" && (
                    <Field
                      as={RadioGroup}
                      name="nzIntendPayOff"
                      label="Do you intend to pay off the lump sum using existing savings, refinancing or returning the asset?"
                      options={yesNoOptions}
                      validate={nzValidateRadioGroupField}
                    />
                  )}
                  <Field
                    as={Input}
                    name="nzTotalCreditLimit"
                    label="Total amount owing on Credit Cards and/or overdrafts"
                    variant="numeric"
                    maxLength="8"
                    helpText="The total owing across all your credit cards and store cards"
                    prefix="$"
                  />

                  <Field
                    as={Input}
                    name="nzTotalAmountOwing"
                    label="Total credit limit on cards and/or overdrafts"
                    variant="numeric"
                    maxLength="8"
                    helpText="The combined limit across all your credit cards"
                    prefix="$"
                  />
                </Stack>
              )}

              <Container width="100%" width-sm="400">
                <FormActions>
                  <FormActions.Primary>
                    <Button type="submit">Next</Button>
                  </FormActions.Primary>
                  <FormActions.Secondary>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        dispatch({
                          type: ActionTypes.SUBMIT_ADDITIONAL_DEBTS,
                          additionalDebts: {
                            nzHaveOtherLoans,
                            nzCurrentLoans,
                            ...formValues,
                          },
                        });
                        navigate(-1);
                      }}
                    >
                      Back
                    </Button>
                  </FormActions.Secondary>
                </FormActions>
              </Container>
              <CancelLink onClick={() => setIsModalOpen(true)} />
            </Stack>
            <Cancel
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              onConfirm={() => {
                navigate("/manage-account");
              }}
            />
          </>
        </Form>
      </Layout.Main>
    </Layout>
  );
};
